// src/shared/dashboard/shell/TopBar.tsx
//
// The phone header has one job: preserve product context without competing with
// the bottom navigation. Product switching stays on the left; account utilities
// stay on the right. Plan/subscription state belongs in Profile rather than a
// fourth chip competing for 375px of width.
import { useEffect, useRef, useState } from "react";
import { GraduationCap, Sparkles } from "lucide-react";
import {
  INSTRUCTOR_LABEL,
  STUDIO_LABEL,
} from "@/shared/dashboard/shell/navLabels";
import { go } from "@/shared/dashboard/shell/navigate";
import {
  LogoMark,
  ProductSwitcher,
} from "@/shared/dashboard/shell/ProductSwitcher";
import { LanguageSelect } from "@/shared/dashboard/shell/LanguageSelect";

// AppShell can remount while moving between routes. Keep the last committed
// product outside the component so those remounts do not look like product
// changes and replay the nav entrance animation.
let lastCommittedVerticalId: string | undefined;

export function TopBar({
  locale,
  userInitial,
  isInstructor = false,
  verticalId,
  showWorkspaceSwitcher = true,
}: {
  locale: string;
  userInitial: string;
  isInstructor?: boolean;
  verticalId?: string;
  showWorkspaceSwitcher?: boolean;
}) {
  const previousVerticalId = useRef(lastCommittedVerticalId);
  const [animatedVerticalId, setAnimatedVerticalId] = useState<
    string | undefined
  >(() =>
    verticalId &&
    lastCommittedVerticalId !== undefined &&
    lastCommittedVerticalId !== verticalId
      ? verticalId
      : undefined,
  );
  const animateProductChange =
    !!verticalId && animatedVerticalId === verticalId;

  useEffect(() => {
    if (!verticalId) return;
    if (
      previousVerticalId.current !== undefined &&
      previousVerticalId.current !== verticalId
    ) {
      setAnimatedVerticalId(verticalId);
    }
    lastCommittedVerticalId = verticalId;
    previousVerticalId.current = verticalId;
  }, [verticalId]);

  return (
    <header
      className="relative z-50 flex min-h-16 items-center justify-between gap-3 border-b border-[var(--line-soft)] bg-[var(--paper-canvas)]/95 px-4 pb-3 backdrop-blur-xl sm:px-6"
      style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
    >
      <div className="min-w-0 flex-1">
        {showWorkspaceSwitcher ? (
          <ProductSwitcher animateProductChange={animateProductChange} />
        ) : (
          <LogoMark className="h-5" tone="ink" />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {isInstructor && verticalId === "general" && (
          <button
            type="button"
            onClick={() => go("/studio")}
            aria-label={STUDIO_LABEL[locale] ?? STUDIO_LABEL.en}
            className="hidden h-11 w-11 cursor-pointer place-items-center text-[var(--ink-tertiary)] transition-colors hover:bg-[var(--paper-sunken)] hover:text-[var(--ink-primary)] min-[440px]:grid"
          >
            <Sparkles size={14} className="shrink-0" />
          </button>
        )}
        {isInstructor && (
          <button
            type="button"
            onClick={() => go("/instructor")}
            aria-label={INSTRUCTOR_LABEL[locale] ?? INSTRUCTOR_LABEL.en}
            className="grid h-11 w-11 cursor-pointer place-items-center text-[var(--ink-tertiary)] transition-colors hover:bg-[var(--paper-sunken)] hover:text-[var(--ink-primary)]"
          >
            <GraduationCap size={14} className="shrink-0" />
          </button>
        )}
        <LanguageSelect />
        <button
          type="button"
          onClick={() => go("/profile")}
          className="ml-1 grid h-11 w-11 cursor-pointer place-items-center rounded-full bg-[var(--ink-primary)] ed-mono text-[12px] text-[var(--ink-inverse)] transition-colors hover:bg-[var(--action-bg-hover)]"
          aria-label="Your profile"
        >
          {userInitial}
        </button>
      </div>
    </header>
  );
}
